import { Elements, type Edges, type EID, type NID, type Nodes } from '@graph';
import type { CommandContext } from './CommandContext';

export function selectAll(context: CommandContext): { selection: Elements } {
  const { doc } = context;
  const selection = Elements.make(doc.graph.nodes, doc.graph.edges);
  const result = { selection };
  context.update(result);
  return result;
}

/**
 * Selects the successors of the currently selected nodes and edges in the document.
 *
 * This command collects all the outgoing edges of the selected nodes and all the target nodes
 * of the selected edges, and adds them to the current selection.
 *
 * @param context - The command context containing the document and current selection.
 * @returns The updated selection including the successors of the selected nodes and edges.
 */
export function selectSuccessors(context: CommandContext): {
  selection: Elements;
} {
  const { doc, selection } = context;

  // Collect all the out edges of the selected nodes
  const addedEdges: Edges = new Set();
  for (const node of selection.nodes) {
    for (const edge of doc.graph.outEdges(node)) {
      addedEdges.add(edge);
    }
  }

  // Collect all the target nodes of the selected edges
  const addedNodes: Nodes = new Set();
  for (const edge of selection.edges) {
    const target = doc.graph.target(edge);
    addedNodes.add(target);
  }

  const newSelection = selection.adding(Elements.make(addedNodes, addedEdges));
  const result = { selection: newSelection };
  context.update(result);
  return result;
}

/**
 * Selects the predecessors of the currently selected nodes and edges in the document.
 *
 * This command collects all the incoming edges of the selected nodes and all the source nodes
 * of the selected edges, and adds them to the current selection.
 *
 * @param context - The command context containing the document and the current selection.
 * @returns The updated selection including the predecessors.
 */
export function selectPredecessors(context: CommandContext): {
  selection: Elements;
} {
  const { doc, selection } = context;

  // Collect all the in edges of the selected nodes
  const addedEdges: Edges = new Set();
  for (const node of selection.nodes) {
    for (const edge of doc.graph.inEdges(node)) {
      addedEdges.add(edge);
    }
  }

  // Collect all the source nodes of the selected edges
  const addedNodes: Nodes = new Set();
  for (const edge of selection.edges) {
    const source = doc.graph.source(edge);
    addedNodes.add(source);
  }

  const newSelection = selection.adding(Elements.make(addedNodes, addedEdges));
  const result = { selection: newSelection };
  context.update(result);
  return result;
}

/**
 * Selects the neighboring elements (nodes and edges) of the currently selected elements in the document.
 *
 * This command collects all the in and out edges of the selected nodes and all the source and target nodes
 * of the selected edges, and adds them to the current selection.
 *
 * @param context - The command context containing the document and the current selection.
 * @returns The updated selection including the neighboring elements.
 */
export function selectNeighbors(context: CommandContext): {
  selection: Elements;
} {
  const selectionWithSuccessors = selectSuccessors(context).selection;
  const selectionWithPredecessors = selectPredecessors(context).selection;
  const selection = selectionWithSuccessors.adding(selectionWithPredecessors);
  const result = { selection };
  context.update(result);
  return result;
}

/**
 * Selects the transitive successors of the currently selected nodes and edges in the document.
 *
 * This command collects all the outgoing edges of the selected nodes and all the target nodes
 * of the selected edges, and adds them to the current selection, transitively.
 *
 * @param context - The command context containing the document and current selection.
 * @returns The updated selection including the successors of the selected nodes and edges.
 */
export function selectAllSuccessors(context: CommandContext): {
  selection: Elements;
} {
  const { doc, selection } = context;
  const graph = doc.graph;
  const nodeQueue: NID[] = [...selection.nodes];
  const edgeQueue: EID[] = [...selection.edges];
  const addedNodes: Nodes = new Set();
  const addedEdges: Edges = new Set();
  const visitedNodes: Nodes = new Set();
  const visitedEdges: Edges = new Set();

  while (nodeQueue.length > 0 || edgeQueue.length > 0) {
    while (nodeQueue.length > 0) {
      const node = nodeQueue.shift();
      if (node && !visitedNodes.has(node)) {
        visitedNodes.add(node);
        for (const edge of graph.outEdges(node)) {
          if (!visitedEdges.has(edge)) {
            edgeQueue.push(edge);
          }
        }
      }
    }

    while (edgeQueue.length > 0) {
      const edge = edgeQueue.shift();
      if (edge && !visitedEdges.has(edge)) {
        visitedEdges.add(edge);
        addedEdges.add(edge);
        const target = graph.target(edge);
        if (target && !visitedNodes.has(target)) {
          addedNodes.add(target);
          nodeQueue.push(target);
        }
      }
    }
  }

  const newSelection = selection.adding(Elements.make(addedNodes, addedEdges));
  const result = { selection: newSelection };
  context.update(result);
  return result;
}

/**
 * Selects the transitive predecessors of the currently selected nodes and edges in the document.
 *
 * This command collects all the incoming edges of the selected nodes and all the source nodes
 * of the selected edges, and adds them to the current selection, transitively.
 *
 * @param context - The command context containing the document and the current selection.
 * @returns The updated selection including the predecessors of the selected nodes and edges.
 */
export function selectAllPredecessors(context: CommandContext): {
  selection: Elements;
} {
  const { doc, selection } = context;
  const graph = doc.graph;
  const nodeQueue: NID[] = [...selection.nodes];
  const edgeQueue: EID[] = [...selection.edges];
  const addedNodes: Nodes = new Set();
  const addedEdges: Edges = new Set();
  const visitedNodes: Nodes = new Set();
  const visitedEdges: Edges = new Set();

  while (nodeQueue.length > 0 || edgeQueue.length > 0) {
    while (nodeQueue.length > 0) {
      const node = nodeQueue.shift();
      if (node && !visitedNodes.has(node)) {
        visitedNodes.add(node);
        for (const edge of graph.inEdges(node)) {
          if (!visitedEdges.has(edge)) {
            edgeQueue.push(edge);
          }
        }
      }
    }

    while (edgeQueue.length > 0) {
      const edge = edgeQueue.shift();
      if (edge && !visitedEdges.has(edge)) {
        visitedEdges.add(edge);
        addedEdges.add(edge);
        const source = graph.source(edge);
        if (source && !visitedNodes.has(source)) {
          addedNodes.add(source);
          nodeQueue.push(source);
        }
      }
    }
  }

  const newSelection = selection.adding(Elements.make(addedNodes, addedEdges));
  const result = { selection: newSelection };
  context.update(result);
  return result;
}

/**
 * Selects the transitive neighbors of the currently selected nodes and edges in the document.
 *
 * This command collects all the incoming and outgoing edges of the selected nodes and all the source and target nodes
 * of the selected edges, and adds them to the current selection, transitively.
 *
 * @param context - The command context containing the document and the current selection.
 * @returns The updated selection including the neighbors of the selected nodes and edges.
 */
export function selectAllNeighbors(context: CommandContext): {
  selection: Elements;
} {
  const { doc, selection } = context;
  const graph = doc.graph;
  const nodeQueue: NID[] = [...selection.nodes];
  const edgeQueue: EID[] = [...selection.edges];
  const addedNodes: Nodes = new Set();
  const addedEdges: Edges = new Set();
  const visitedNodes: Nodes = new Set();
  const visitedEdges: Edges = new Set();

  while (nodeQueue.length > 0 || edgeQueue.length > 0) {
    while (nodeQueue.length > 0) {
      const node = nodeQueue.shift();
      if (node && !visitedNodes.has(node)) {
        visitedNodes.add(node);
        for (const edge of graph.inEdges(node)) {
          if (!visitedEdges.has(edge)) {
            edgeQueue.push(edge);
          }
        }
        for (const edge of graph.outEdges(node)) {
          if (!visitedEdges.has(edge)) {
            edgeQueue.push(edge);
          }
        }
      }
    }

    while (edgeQueue.length > 0) {
      const edge = edgeQueue.shift();
      if (edge && !visitedEdges.has(edge)) {
        visitedEdges.add(edge);
        addedEdges.add(edge);
        const source = graph.source(edge);
        if (source && !visitedNodes.has(source)) {
          addedNodes.add(source);
          nodeQueue.push(source);
        }
        const target = graph.target(edge);
        if (target && !visitedNodes.has(target)) {
          addedNodes.add(target);
          nodeQueue.push(target);
        }
      }
    }
  }

  const newSelection = selection.adding(Elements.make(addedNodes, addedEdges));
  const result = { selection: newSelection };
  context.update(result);
  return result;
}
